/**
 * `AuthProvider` — a React mirror of the cookie state, plus the session
 * lifecycle the cookies alone cannot express.
 *
 * It deliberately owns very little. The cookie is the source of truth and
 * `getAuthState()` reads it synchronously, so route guards never consult this
 * context. What lives here is the part that needs a component tree: the boot
 * rehydration gate, the proactive refresh timer, the wake-up recovery
 * listeners, and sign-in / sign-out as actions a screen can call.
 *
 * The gate is the one thing this provider withholds the tree for, and only in
 * the one case that needs it: an access token that has expired while the
 * refresh token is still alive. See `isBooting` below.
 *
 * Every network call goes through a GENERATED hook — the
 * mutate functions are held in refs so the single-flight wrapper below can call
 * them from a timer without re-creating itself on every render.
 */
import { useSessionRevoke } from "@sportsmet/auth-sdk/private";
import { useSessionRefresh } from "@sportsmet/auth-sdk/public";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { markIntroTourSeen } from "../features/welcome/introTourStore";
import { getAuthState, type typeAuthState } from "./getAuthState";
import {
  clearSessionTokens,
  readRefreshToken,
  writeSessionTokens,
} from "./sessionCookies";

/**
 * Refresh this far ahead of expiry. Long enough that a slow network still
 * lands the new token before the old one dies, short enough that a tab left
 * open overnight is not re-minting tokens every few minutes.
 */
export const PROACTIVE_REFRESH_LEAD_MS = 60_000;

/**
 * How long boot waits for the renewal below before it lets the router mount
 * anyway.
 *
 * The wait is spent on a blank frame, so it is short. When it runs out the
 * cookies are untouched and the app carries on exactly as it did before this
 * gate existed: the guard reads a dead access token and sends the fan to
 * sign-in. A slow network costs the old behavior, never a longer wait.
 */
export const BOOT_REFRESH_TIMEOUT_MS = 5_000;

/**
 * The SAME lock name `@sportsmet/auth-sdk`'s reactive 401 retry uses. Sharing
 * it is the whole point: the refresh token has no grace window, so two
 * concurrent rotations — one proactive from this timer, one reactive from a
 * request that raced it — would invalidate each other and sign the fan out
 * mid-session. Browsers without the Web Locks API fall back to the in-tab
 * promise slot below, which covers the common case on its own.
 */
const REFRESH_LOCK_NAME = "sportsmet-auth-refresh";

type typeLockManagerLike = {
  request?: (name: string, fn: () => Promise<boolean>) => Promise<boolean>;
};

function runExclusive(fn: () => Promise<boolean>): Promise<boolean> {
  const locks = (globalThis as { navigator?: { locks?: typeLockManagerLike } })
    .navigator?.locks;
  if (locks && typeof locks.request === "function") {
    return locks.request(REFRESH_LOCK_NAME, fn);
  }
  return fn();
}

export type typeAuthContext = {
  state: typeAuthState;
  /** Called by the sign-in screen once OTP verification has returned tokens. */
  signIn: (tokens: { accessToken: string; refreshToken: string }) => void;
  /** Revokes this session server-side, then clears every cached query. */
  signOut: () => Promise<void>;
  /** Resolves true when a usable access token is in place afterwards. */
  refreshSession: () => Promise<boolean>;
  isSigningOut: boolean;
};

const AuthContext = createContext<typeAuthContext | null>(null);

function isSameState(a: typeAuthState, b: typeAuthState): boolean {
  if (a.status !== b.status) return false;
  if (a.status !== "signedIn" || b.status !== "signedIn") return true;
  return (
    a.user.userId === b.user.userId &&
    a.user.sessionId === b.user.sessionId &&
    a.expiresAtMs === b.expiresAtMs
  );
}

export const AuthProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactNode => {
  const queryClient = useQueryClient();
  const refreshMutation = useSessionRefresh();
  const revokeMutation = useSessionRevoke();

  const [state, setState] = useState<typeAuthState>(() => getAuthState());
  const inFlightRefresh = useRef<Promise<boolean> | null>(null);

  /**
   * True only when this browser is carrying a refresh token that the access
   * token has outlived, which is every reload more than one access-token TTL
   * (15 minutes) after the last mint.
   *
   * Nothing may render until that renewal settles. `requireSignedIn` reads the
   * cookie synchronously, on purpose, so a router mounted first redirects to
   * /sign-in and the fan never sees the session they still have. Read the
   * tokens AFTER the refresh, never before, which is the same order
   * `packages/mobile-core`'s `SessionProvider` boots in and the same "access
   * OR refresh counts as a session" rule `apps/admin` already applies.
   *
   * A signed-out visitor and a live access token both start false, so the
   * common cases pay nothing for this.
   */
  const [isBooting, setIsBooting] = useState<boolean>(() => {
    if (getAuthState().status === "signedIn") return false;
    const refreshToken = readRefreshToken();
    return refreshToken !== undefined && refreshToken !== "";
  });

  const refreshFn = useRef(refreshMutation.mutateAsync);
  const revokeFn = useRef(revokeMutation.mutateAsync);
  useEffect(() => {
    refreshFn.current = refreshMutation.mutateAsync;
    revokeFn.current = revokeMutation.mutateAsync;
  }, [refreshMutation.mutateAsync, revokeMutation.mutateAsync]);

  /** Re-reads the cookie. A no-op when nothing meaningful changed, so a wake-up
   * event on an idle tab does not re-render the whole app for nothing. */
  const syncFromCookies = useCallback(() => {
    setState((current) => {
      const next = getAuthState();
      return isSameState(current, next) ? current : next;
    });
  }, []);

  const refreshSession = useCallback((): Promise<boolean> => {
    const existing = inFlightRefresh.current;
    if (existing) return existing;

    const run = async (): Promise<boolean> => {
      const refreshToken = readRefreshToken();
      if (refreshToken === undefined || refreshToken === "") {
        clearSessionTokens();
        return false;
      }
      try {
        const result = await refreshFn.current({ refreshToken });
        const data = result.data;
        if (result.error) {
          // "The server refused this token" and "the call failed" are two
          // different outcomes and only the first ends the session. The SDK's
          // transport never throws: a dead network, a timeout and an empty
          // non-2xx all come back as an INTERNAL_ERROR envelope
          // (packages/auth-sdk/src/public/sdk-helpers/call.ts), so the catch
          // below never sees them and this branch is the only place the two
          // can be told apart. auth-api refuses exactly one way, with the ERROR
          // CODE `UNAUTHENTICATED`, so every other code keeps the cookies and
          // lets the next wake-up event try again. Clearing on any error at all
          // is what signed a fan out over a 429 from the per-IP rate limit on
          // /session/refresh, or over a 502 from a rolling deploy.
          //
          // The CODE, never the status. `@sportsmet/errors` classes
          // UNAUTHENTICATED as "forbidden" and so answers 403 with it, and 403
          // is shared with FORBIDDEN, OAUTH_TOKEN_REJECTED and a failed bot
          // check, none of which mean this refresh token is dead. Same rule and
          // same reasoning as `wasRefused` in the SDK's
          // sdk-helpers/refreshAccessToken.ts.
          if (result.error.code === "UNAUTHENTICATED") clearSessionTokens();
          return false;
        }
        if (!data?.accessToken) {
          // A 200 carrying nothing usable. Not a refusal either, so the
          // cookies stay put.
          return false;
        }
        writeSessionTokens({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
        });
        return true;
      } catch {
        // Only reachable if the mutation itself throws instead of resolving an
        // envelope. Same rule as above: not a refusal, so keep the tokens.
        return false;
      }
    };

    const promise = runExclusive(run)
      .then((didRefresh) => {
        syncFromCookies();
        return didRefresh;
      })
      .finally(() => {
        inFlightRefresh.current = null;
      });
    inFlightRefresh.current = promise;
    return promise;
  }, [syncFromCookies]);

  // Boot rehydration. Runs once, only for the browser described by
  // `isBooting` above, and opens the gate whichever way the renewal goes: a
  // fresh access token means the fan lands where they asked for, and a refused
  // one has already cleared the cookies so the guard sends them to sign-in,
  // which is the outcome the guard is for.
  useEffect(() => {
    if (!isBooting) return;
    // Held on an object rather than a plain `let`: the flag is only ever
    // cleared from the cleanup below, and the checker narrows a plain local to
    // the literal true at every line that has not visibly run that callback.
    const mounted = { current: true };
    const openGate = (): void => {
      if (mounted.current) setIsBooting(false);
    };
    // The net. Without it a refresh that never SETTLES (a hung request, not a
    // failed one) leaves `isBooting` true forever and the render below puts the
    // whole app behind a blank document. `openGate` is idempotent, so it does
    // not matter which of the two gets there first.
    const handle = setTimeout(openGate, BOOT_REFRESH_TIMEOUT_MS);
    void refreshSession().finally(() => {
      openGate();
    });
    // The ONE place the timer is disarmed, deliberately. `openGate` flips
    // `isBooting`, which is a dependency of this effect, so settling normally
    // re-runs the effect and reaches this cleanup anyway; unmounting mid-flight
    // reaches it too. An extra `clearTimeout` in the `.finally` above looked
    // like defense in depth but could not be told apart from this line by any
    // test, because this line always beats it to the work.
    return () => {
      mounted.current = false;
      clearTimeout(handle);
    };
  }, [isBooting, refreshSession]);

  // Proactive refresh. Rescheduled whenever the session changes, which includes
  // every successful refresh — that is what makes it a repeating cycle without
  // an interval that would keep firing after sign-out.
  useEffect(() => {
    if (state.status !== "signedIn") return;
    const delay = Math.max(
      0,
      state.expiresAtMs - Date.now() - PROACTIVE_REFRESH_LEAD_MS,
    );
    const handle = setTimeout(() => {
      void refreshSession();
    }, delay);
    return () => {
      clearTimeout(handle);
    };
  }, [state, refreshSession]);

  // Recovery. A backgrounded tab's timers are throttled or suspended outright,
  // so coming back to a laptop lid that was shut for an hour has to re-check on
  // the way in rather than trusting a timer that never fired.
  useEffect(() => {
    const recover = (): void => {
      const next = getAuthState();
      if (
        next.status === "signedIn" &&
        next.expiresAtMs - Date.now() > PROACTIVE_REFRESH_LEAD_MS
      ) {
        syncFromCookies();
        return;
      }
      void refreshSession();
    };
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") recover();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", recover);
    window.addEventListener("online", recover);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", recover);
      window.removeEventListener("online", recover);
    };
  }, [refreshSession, syncFromCookies]);

  // A browser that has been signed in belongs to a returning fan, who should
  // never get the first-visit intro tour after a later sign-out, even if they
  // first arrived through a deep link (ISSUE-1406). Keyed on the state rather
  // than on `signIn`, so a session that was already live before the tour
  // shipped (one that never passes through `signIn`) is remembered too.
  useEffect(() => {
    if (state.status === "signedIn") markIntroTourSeen();
  }, [state.status]);

  const signIn = useCallback(
    (tokens: { accessToken: string; refreshToken: string }) => {
      writeSessionTokens(tokens);
      syncFromCookies();
    },
    [syncFromCookies],
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await revokeFn.current({ doRevokeAllSessions: false });
    } catch {
      // Revoking is best-effort. A fan who taps "Sign out" on a flaky
      // connection must still end up signed out locally, not stuck on a screen
      // that looks like the button did nothing.
    }
    clearSessionTokens();
    syncFromCookies();
    // Every cached query was fetched as the previous user. Dropping the cache
    // is what stops the next sign-in from painting somebody else's data for a
    // frame before it refetches.
    queryClient.clear();
  }, [queryClient, syncFromCookies]);

  const value = useMemo<typeAuthContext>(
    () => ({
      state,
      signIn,
      signOut,
      refreshSession,
      isSigningOut: revokeMutation.isPending,
    }),
    [state, signIn, signOut, refreshSession, revokeMutation.isPending],
  );

  // Nothing while the boot renewal is in flight. This is the same empty
  // document the browser already shows for the milliseconds before React
  // mounts, held for one API call longer, and it is what stops the router from
  // deciding the session is over before anyone has asked the server.
  return <AuthContext value={value}>{isBooting ? null : children}</AuthContext>;
};

export function useAuth(): typeAuthContext {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      "useAuth must be used inside an <AuthProvider>. Mount one once at the app root.",
    );
  }
  return context;
}
